import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { sort, extension, ExtensionPriority, command, helper, PlainExtension, isEmptyObject, assert, environment, getTextSelection, within } from '@remirror/core';
import { DecorationSet, Decoration } from '@remirror/pm/view';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { selectionPositioner } from '@remirror/extension-positioner';

var ActionType;

(function (ActionType) {
  ActionType[ActionType["ADD_ANNOTATION"] = 0] = "ADD_ANNOTATION";
  ActionType[ActionType["REDRAW_ANNOTATIONS"] = 1] = "REDRAW_ANNOTATIONS";
  ActionType[ActionType["REMOVE_ANNOTATIONS"] = 2] = "REMOVE_ANNOTATIONS";
  ActionType[ActionType["SET_ANNOTATIONS"] = 3] = "SET_ANNOTATIONS";
  ActionType[ActionType["UPDATE_ANNOTATION"] = 4] = "UPDATE_ANNOTATION";
})(ActionType || (ActionType = {}));

/**
 * Creates non-overlapping segments based on overlapping annotations.
 *
 * Prosemirror combines overlapping inline decorations by creating segments in
 * which the props of each overlapping decoration are merged.
 *
 * The Prosemirror approach doesn't allow to calculate styles based on multiple
 * annotations, e.g. a darker background shade if there are multiple
 * annotations.
 *
 * To overcome this limitations, this method calculates non-overlapping segments
 * with overlapping annotations. These segments can be used to create
 * ProseMirror decorations with a style that reflects all all annotations within
 * the segment.
 *
 * Approach was confirmed by Marijn:
 * https://discuss.prosemirror.net/t/how-to-style-overlapping-inline-decorations/3162
 */
function toSegments(annotations) {
  // Keep track of the segments which will be returned by this function.
  var segments = []; // This holds the index of the `start` and `end` annotations for each
  // position.

  var positionMap = new Map(); // Build up the index items with the provided annotations.

  var _iterator = _createForOfIteratorHelper(annotations),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _positionMap$get, _positionMap$get2;

      var annotation = _step.value;
      // Get the already added annotations for the positions provided.
      var currentFrom = (_positionMap$get = positionMap.get(annotation.from)) !== null && _positionMap$get !== void 0 ? _positionMap$get : [];
      var currentTo = (_positionMap$get2 = positionMap.get(annotation.to)) !== null && _positionMap$get2 !== void 0 ? _positionMap$get2 : [];
      positionMap.set(annotation.from, [...currentFrom, {
        type: 'start',
        annotation
      }]);
      positionMap.set(annotation.to, [...currentTo, {
        type: 'end',
        annotation
      }]);
    } // Sort from the smallest position to the largest position (a-z);

  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var sortedPositions = sort([...positionMap.entries()], (_ref, _ref2) => {
    var _ref3 = _slicedToArray(_ref, 1),
        a = _ref3[0];

    var _ref4 = _slicedToArray(_ref2, 1),
        z = _ref4[0];

    return a - z;
  }); // Tracks the annotations active in the currently analyzed segment

  var activeAnnotations = []; // Tracks the last from state

  var from = 0; // Loop through the available items.

  var _iterator2 = _createForOfIteratorHelper(sortedPositions),
      _step2;

  try {
    var _loop = function _loop() {
      var _step2$value = _slicedToArray(_step2.value, 2),
          to = _step2$value[0],
          items = _step2$value[1];

      var startAnnotations = items.filter(item => item.type === 'start').map(item => item.annotation);
      var endIds = new Set(items.filter(item => item.type === 'end').map(item => item.annotation.id));

      if (activeAnnotations.length > 0) {
        segments.push({
          from,
          to,
          annotations: activeAnnotations
        });
      } // Update the from position to point to the current `to` for the next
      // iteration.


      from = to; // Update the active annotations with the latest start annotations and
      // remove any annotations that have reached the end.

      activeAnnotations = [...activeAnnotations, ...startAnnotations].filter( // Remove any annotation that is at the end of the annotation group.
      annotation => !endIds.has(annotation.id));
    };

    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      _loop();
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return segments;
}

class AnnotationState {
  /**
   * Cache of annotations being currently shown
   */

  /**
   * Decorations are computed based on the annotations. The state contains a
   * copy of the decoration for performance optimization.
   */
  constructor(getStyle, store) {
    _defineProperty(this, "annotations", []);

    _defineProperty(this, "decorationSet", DecorationSet.empty);

    this.getStyle = getStyle;
    this.store = store;
  }

  addAnnotation(addAction) {
    // FIXME: Review and remove explicit cast.
    var annotation = _objectSpread({
      from: addAction.from,
      to: addAction.to
    }, addAction.annotationData);

    this.store.addAnnotation(annotation);
  }

  updateAnnotation(updateAction) {
    this.store.updateAnnotation(updateAction.annotationId, updateAction.annotationData);
  }

  removeAnnotations(removeAction) {
    this.store.removeAnnotations(removeAction.annotationIds);
  }

  setAnnotations(setAction) {
    this.store.setAnnotations(setAction.annotations);
  }

  formatAnnotations() {
    return this.store.formatAnnotations();
  }

  createDecorations(tr) {
    var annotations = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    // Recalculate decorations when annotations changed
    var decos = toSegments(annotations).map(segment => {
      var classNames = segment.annotations.map(a => a.className).filter(className => className);
      var style = this.getStyle(segment.annotations);
      return Decoration.inline(segment.from, segment.to, {
        class: classNames.length > 0 ? classNames.join(' ') : undefined,
        style
      });
    });
    return DecorationSet.create(tr.doc, decos);
  }

  apply(_ref) {
    var tr = _ref.tr,
        action = _ref.action;
    var actionType = action === null || action === void 0 ? void 0 : action.type;

    if (!action && !tr.docChanged) {
      return this;
    }

    if (actionType !== undefined) {
      if (actionType === ActionType.ADD_ANNOTATION) {
        this.addAnnotation(action);
      }

      if (actionType === ActionType.UPDATE_ANNOTATION) {
        this.updateAnnotation(action);
      }

      if (actionType === ActionType.REMOVE_ANNOTATIONS) {
        this.removeAnnotations(action);
      }

      if (actionType === ActionType.SET_ANNOTATIONS) {
        this.setAnnotations(action);
      }

      this.annotations = this.formatAnnotations();
      this.decorationSet = this.createDecorations(tr, this.annotations);
    } else {
      // Adjust cached annotation positions based on changes in the editor, e.g.
      // if new text was added before the decoration.
      //
      // Note: If you see annotations getting removed here check the source of
      // the transaction and whether it contains any unexpected steps. In particular
      // 'replace' steps that modify the entire document range, such as the one
      // used by the Yjs extension for supporting `undo`, can cause issues.
      // Consider using the `disableUndo` option of the Yjs extension, if you are
      // using both the Yjs and Annotations extensions.
      this.annotations = this.annotations.map(annotation => _objectSpread(_objectSpread({}, annotation), {}, {
        // 1 indicates that the annotation isn't extended when the user types
        // at the beginning of the annotation
        from: tr.mapping.map(annotation.from, 1),
        // -1 indicates that the annotation isn't extended when the user types
        // at the end of the annotation
        to: tr.mapping.map(annotation.to, -1)
      })) // Remove annotations for which all containing content was deleted
      .filter(annotation => annotation.to !== annotation.from); // Update the store with the updated annotation positions, and the remove ones

      this.store.setAnnotations(this.annotations);
      this.decorationSet = this.decorationSet.map(tr.mapping, tr.doc);
    }

    return this;
  }

}

var _excluded = ["from", "to"],
    _excluded2 = ["from", "to"];
class MapLikeAnnotationStore {
  /**
   * @param map a custom map-like object for storing internal annotations
   */
  constructor() {
    var map = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Map();
    var positionToStored = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : pos => pos;
    var positionFromStored = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : storedPos => storedPos;
    this.map = map;
    this.positionToStored = positionToStored;
    this.positionFromStored = positionFromStored;
  }

  addAnnotation(_ref) {
    var from = _ref.from,
        to = _ref.to,
        annotation = _objectWithoutProperties(_ref, _excluded);

    // XXX: Review cast
    var storedAnnotation = _objectSpread({
      from: this.positionToStored(from),
      to: this.positionToStored(to)
    }, annotation);

    this.map.set(annotation.id, storedAnnotation);
  }

  updateAnnotation(id, data) {
    var existing = this.map.get(id);

    if (!existing) {
      return;
    }

    this.map.set(id, _objectSpread(_objectSpread({}, existing), data));
  }

  removeAnnotations(ids) {
    ids.forEach(id => {
      this.map.delete(id);
    });
  }

  setAnnotations(annotations) {
    // `clear` is optional for historic reasons: Older versions of the Yjs maps
    // didn't provide it.
    if (typeof this.map.clear === 'function') {
      this.map.clear();
    } else {
      this.map.forEach(annotation => this.map.delete(annotation.id));
    }

    annotations.forEach(annotation => {
      this.addAnnotation(annotation);
    });
  }

  formatAnnotations() {
    var annotations = [];
    this.map.forEach(_ref2 => {
      var storedFrom = _ref2.from,
          storedTo = _ref2.to,
          storedData = _objectWithoutProperties(_ref2, _excluded2);

      var from = this.positionFromStored(storedFrom);
      var to = this.positionFromStored(storedTo);

      if (!from || !to) {
        return;
      } // XXX: Review cast


      var annotation = _objectSpread({
        from,
        to
      }, storedData);

      annotations.push(annotation);
    });
    return annotations;
  }

}

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _class, _class2;

/**
 * Computes a background color based on how many overlapping annotations are in
 * a segment. The more annotations, the darker the background. This gives the
 * illusion that annotations are above each other.
 */
function defaultGetStyle(annotations) {
  // Consider up to 5 overlapping annotations
  var backgroundShade = Math.min(annotations.length, 5) / 5;
  var notBlue = 200 * (1 - backgroundShade) + 55;
  return "background: rgb(".concat(notBlue, ", ").concat(notBlue, ", 255);");
}
/**
 * This extension allows to annotate the content in your editor.
 *
 * Extend the Annotation interface to store application specific information
 * like tags or color.
 */


var AnnotationExtension = (_dec = extension({
  defaultOptions: {
    getStyle: defaultGetStyle,
    blockSeparator: undefined,
    getStore: () => new MapLikeAnnotationStore(),
    // Obsolete options
    getMap: undefined,
    transformPosition: undefined,
    transformPositionBeforeRender: undefined
  },
  defaultPriority: ExtensionPriority.Low
}), _dec2 = command(), _dec3 = command(), _dec4 = command(), _dec5 = command(), _dec6 = command(), _dec7 = helper(), _dec8 = helper(), _dec9 = helper(), _dec(_class = (_class2 = class AnnotationExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "enrichText", annotation => {
      var _this$store$getState = this.store.getState(),
          doc = _this$store$getState.doc; // Gracefully handle if annotations point to positions outside the content
      // This can happen if content/annotations are set at different points.


      var text = annotation.to <= doc.content.size ? doc.textBetween(annotation.from, annotation.to, this.options.blockSeparator) : undefined;
      return _objectSpread(_objectSpread({}, annotation), {}, {
        text
      });
    });
  }

  get name() {
    return 'annotation';
  }

  onSetOptions(props) {
    var pickChanged = props.pickChanged;
    var changedPluginOptions = pickChanged(['getStore', 'getMap', 'transformPosition', 'transformPositionBeforeRender']);

    if (!isEmptyObject(changedPluginOptions)) {
      this.store.updateExtensionPlugins(this);
    }
  }
  /**
   * Create the custom code block plugin which handles the delete key amongst
   * other things.
   */


  createPlugin() {
    var store;

    if (this.options.getMap) {
      assert(environment.isProduction || !this.options.getStore, 'Must not provide both "getMap" and "getStore"');
      store = new MapLikeAnnotationStore(this.options.getMap(), this.options.transformPosition, this.options.transformPositionBeforeRender);
    } else {
      store = this.options.getStore();
    }

    var pluginState = new AnnotationState(this.options.getStyle, store);
    return {
      state: {
        init() {
          return pluginState;
        },

        apply(tr) {
          var action = tr.getMeta(AnnotationExtension.name);
          return pluginState.apply({
            tr,
            action
          });
        }

      },
      props: {
        decorations(state) {
          return this.getState(state).decorationSet;
        }

      }
    };
  }
  /**
   * Adds an annotation spanning the currently selected content.
   *
   * In order to use this command make sure you have the
   * [[`AnnotationExtension`]] added to your editor.
   *
   * @param annotationData - the data for the provided annotation.
   */


  addAnnotation(annotationData) {
    return _ref => {
      var tr = _ref.tr,
          dispatch = _ref.dispatch;
      var _tr$selection = tr.selection,
          empty = _tr$selection.empty,
          from = _tr$selection.from,
          to = _tr$selection.to;

      if (empty) {
        return false;
      }

      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setMeta(AnnotationExtension.name, {
        type: ActionType.ADD_ANNOTATION,
        from,
        to,
        annotationData
      }));
      return true;
    };
  }
  /**
   * Updates an existing annotation with a new value.
   *
   * In order to use this command make sure you have the
   * [[`AnnotationExtension`]] added to your editor.
   *
   * @param id - the annotation id to update.
   * @param annotationDataWithoutId - the annotation data without the id.
   */


  updateAnnotation(id, annotationDataWithoutId) {
    return _ref2 => {
      var tr = _ref2.tr,
          dispatch = _ref2.dispatch;

      if (dispatch) {
        var annotationData = _objectSpread(_objectSpread({}, annotationDataWithoutId), {}, {
          id
        });

        var action = {
          type: ActionType.UPDATE_ANNOTATION,
          annotationId: id,
          annotationData
        };
        dispatch(tr.setMeta(AnnotationExtension.name, action));
      }

      return true;
    };
  }
  /**
   * Removes a list of annotations.
   *
   * In order to use this command make sure you have the
   * [[`AnnotationExtension`]] added to your editor.
   *
   * @param annotationIds - the ids of the annotations to be removed.
   */


  removeAnnotations(annotationIds) {
    return _ref3 => {
      var tr = _ref3.tr,
          dispatch = _ref3.dispatch;
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setMeta(AnnotationExtension.name, {
        type: ActionType.REMOVE_ANNOTATIONS,
        annotationIds
      }));
      return true;
    };
  }
  /**
   * Sets the annotation. Use this to initialize the extension based on loaded
   * data.
   *
   * In order to use this command make sure you have the
   * [[`AnnotationExtension`]] added to your editor.
   *
   * @param annotations - the initial annotation to be set.
   */


  setAnnotations(annotations) {
    return _ref4 => {
      var tr = _ref4.tr,
          dispatch = _ref4.dispatch;
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setMeta(AnnotationExtension.name, {
        type: ActionType.SET_ANNOTATIONS,
        annotations
      }));
      return true;
    };
  }
  /**
   * Forcefully redraws the annotations
   *
   * Call this function if the styling of the annotations changes.
   *
   * @see
   * https://discord.com/channels/726035064831344711/745695557976195072/759715559477870603
   */


  redrawAnnotations() {
    return _ref5 => {
      var tr = _ref5.tr,
          dispatch = _ref5.dispatch;
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setMeta(AnnotationExtension.name, {
        type: ActionType.REDRAW_ANNOTATIONS
      }));
      return true;
    };
  }
  /**
   * @returns all annotations in the editor.
   *
   * In order to use this helper make sure you have the
   * [[`AnnotationExtension`]] added to your editor.
   */


  getAnnotations() {
    var state = this.getPluginState(); // Enrich text at annotation

    return state.annotations.map(this.enrichText);
  }
  /**
   * @param pos - the position in the root document to find annotations.
   * @param includeEdges - whether to match annotations that start or end exactly on the given pos
   *
   * @returns all annotations at a specific position in the editor.
   *
   * In order to use this helper make sure you have the
   * [[`AnnotationExtension`]] added to your editor.
   */


  getAnnotationsAt(pos) {
    var includeEdges = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var annotations = [];

    var _this$store$getState2 = this.store.getState(),
        doc = _this$store$getState2.doc,
        selection = _this$store$getState2.selection;

    var state = this.getPluginState();

    var _getTextSelection = getTextSelection(pos !== null && pos !== void 0 ? pos : selection, doc),
        from = _getTextSelection.from,
        to = _getTextSelection.to;

    var _iterator = _createForOfIteratorHelper(state.annotations),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var annotation = _step.value;

        if (within(from, annotation.from, annotation.to) || within(to, annotation.from, annotation.to) || within(annotation.from, from, to) || within(annotation.to, from, to)) {
          if (includeEdges) {
            annotations.push(this.enrichText(annotation));
          } else if (annotation.from !== from && annotation.to !== to) {
            annotations.push(this.enrichText(annotation));
          }
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return annotations;
  }
  /**
   * @param pos - the optional selection to check for, if left undefined it
   * default to the current selection
   *
   * @returns true if the selection includes an annotation or is included within
   * an annotation.
   *
   * In order to use this helper make sure you have the
   * [[`AnnotationExtension`]] added to your editor.
   */


  selectionHasAnnotation(pos) {
    return this.getAnnotationsAt(pos).length > 0;
  }
  /**
   * Enrich text at annotation
   */


}, (_applyDecoratedDescriptor(_class2.prototype, "addAnnotation", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "addAnnotation"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "updateAnnotation", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "updateAnnotation"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "removeAnnotations", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "removeAnnotations"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setAnnotations", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "setAnnotations"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "redrawAnnotations", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "redrawAnnotations"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getAnnotations", [_dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "getAnnotations"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getAnnotationsAt", [_dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "getAnnotationsAt"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "selectionHasAnnotation", [_dec9], Object.getOwnPropertyDescriptor(_class2.prototype, "selectionHasAnnotation"), _class2.prototype)), _class2)) || _class);

/**
 * Render a positioner which captures the selected annotation.
 *
 * @remarks
 *
 * This extends the selection positioner. The difference is that the from and to
 * coordinates are picked from shortest annotation selected.
 */
function createCenteredAnnotationPositioner(getAnnotationsAt) {
  return selectionPositioner.clone({
    getActive: props => {
      var state = props.state,
          view = props.view;

      if (!state.selection.empty) {
        return [];
      }

      var annotations = getAnnotationsAt(state.selection.from);

      if (annotations.length === 0) {
        return [];
      } // Using the shortest annotation allows users to select the other
      // overlapping annotation. If we were to use e.g. the longest annotation,
      // there is the possibility that the shorter annotations aren't selectable
      // because they might be fully overlapped by the longer annotation.


      var shortestAnnotation = annotations.sort((annotation1, annotation2) => {
        var _annotation1$text, _annotation2$text;

        return ((_annotation1$text = annotation1.text) !== null && _annotation1$text !== void 0 ? _annotation1$text : '').length - ((_annotation2$text = annotation2.text) !== null && _annotation2$text !== void 0 ? _annotation2$text : '').length;
      })[0];

      if (!shortestAnnotation) {
        return [];
      }

      var from = view.coordsAtPos(shortestAnnotation.from);
      var to = view.coordsAtPos(shortestAnnotation.to);
      return [{
        from,
        to
      }];
    }
  });
}

export { AnnotationExtension, createCenteredAnnotationPositioner };
